<template>
    <div v-if="warningDialog">
        <Dialog
            :visible="true"
            :modal="true"
            :closable="false"
            :close-on-escape="false"
            @hide="autoLogoutStore.resetTimer(true)">
            <div class="confirmation-content">
                <h1 class="text-center text-6xl">{{ getWarningTimerFormatted }}</h1>
                <h2 class="text-lg text-orange-600">
                    <i class="pi pi-exclamation-triangle text-2xl pr-2"/>
                    <span class="text-2xl">{{ t('lbl_logged_out', "You're about to be logged out.") }}</span>
                </h2>
                <p v-html="t('msg_logged_out_dialog', 'You have been idle for some time and to keep your account secure,<br> we will log you out.')"></p>
            </div>
            <template #footer>
                <Button :label="t('btn_logout', 'Log Out')"
                        class="p-button p-button-outlined p-button-secondary"
                        @click="logout"/>
                <Button :label="t('btn_stay_signed_in', 'Stay Signed In')"
                        class="p-button"
                        @click="staySignedIn"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import {onMounted, ref, computed, watch, onBeforeUnmount} from "vue";
import {useRouter} from "vue-router";
import t from "@/service/Translate";
import {calculateHours, calculateMinutes, calculateSeconds, formattedTimer} from "@/service/DateService";
import UserAuthenticationService from "@/service/UserAuthenticationService";
import {useAutoLogout} from "@/stores/auto-logout";
import ls from "@/stores/local-storage"

export default {
    name: "NimbusAutoLogout",
    setup() {
        const autoLogoutStore = useAutoLogout();
        const router = useRouter()
        const userService = new UserAuthenticationService();

        const warningDialog = ref(false);
        const localWarningTimer = ref(autoLogoutStore.warningTimerInterval);

        const warningHours = computed(() => calculateHours(localWarningTimer.value));
        const warningMinutes = computed(() => calculateMinutes(localWarningTimer.value));
        const warningSeconds = computed(() => calculateSeconds(localWarningTimer.value));
        const getWarningTimerFormatted = computed(() => formattedTimer(warningHours.value, warningMinutes.value, warningSeconds.value));

        // button to stay signed in
        const staySignedIn = async () => {
            await autoLogoutStore.handleIdleModalChoice(true);
        }

        // button to logout
        const logout = async () => {
            await autoLogoutStore.handleIdleModalChoice(false);
        }

        onMounted(() => {
            if (userService.isAuthenticated()) {
                // Initialize from an existing state
                const existingWarningState = ls.get('al-warning-state');
                if (existingWarningState) {
                    const warningData = JSON.parse(existingWarningState);
                    if (warningData.isWarned) {
                        warningDialog.value = true;
                        localWarningTimer.value = warningData.remainingTime || autoLogoutStore.warningTimerInterval;
                    }
                }

                // Initialize storage listener for cross-tab communication
                autoLogoutStore.initStorageListener();
                
                // Start the timer when the component is mounted
                autoLogoutStore.autoStartTimer();
                autoLogoutStore.setActiveTimer();
            }
        });

        // Watch for changes in the store's warning state
        watch(() => autoLogoutStore.isTimeWarned, (newValue) => {
            if (userService.isAuthenticated()) {
                warningDialog.value = newValue;
                if (newValue) {
                    localWarningTimer.value = autoLogoutStore.warningTimerInterval;
                }
            }
        });

        // Watch for changes in the store's warning timer interval (cross-tab sync)
        watch(() => autoLogoutStore.warningTimerInterval, (newValue) => {
            if (warningDialog.value) {
                localWarningTimer.value = newValue;
            }
        });

        // Watch for modal state changes from other tabs
        watch(() => autoLogoutStore.showIdleWarningModal, (newValue) => {
            warningDialog.value = newValue;
            if (newValue) {
                localWarningTimer.value = autoLogoutStore.warningTimerInterval;
            }
        });

        onBeforeUnmount(() => {
            if (!userService.isAuthenticated()) {
                autoLogoutStore.resetStoreValues();
                autoLogoutStore.resetActiveTimerValues();
            }
        });

        return {
            /**
             * Constants
             */
            t,
            autoLogoutStore,
            warningDialog,
            getWarningTimerFormatted,
            /**
             * functions
             */
            staySignedIn,
            logout
        }
    }
}
</script>

<style scoped>

</style>
